import React, { useState } from "react";
import { Grid, Stack } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import Typography from "@mui/material/Typography";
import styled from "styled-components";
import CaseAnalysisCategoryToggle from "src/scenes/case-analysis/case-analysis-category-toggle";
import TeamPicker from "src/scenes/case-analysis/case-analysis-team-picker";
import { TeamsProvider } from "src/scenes/teams/teams-provider";
import CaseAnalysisRadio from "src/scenes/case-analysis/case-analysis-radio";
import CaseAnalysisMainSection from "src/scenes/case-analysis/case-analysis-main-section";
import CaseAnalysisGraphSection from "src/scenes/case-analysis/case-analysis-graph-section";
import { ActiveIntegrationsProvider } from "src/scenes/dashboard/active-integrations-provider";
import DemoBarPersonal from "src/components/demo-bar-personal";
import DemoBarDemo from "src/components/demo-bar-demo";
import Portal from "src/components/portal";
import { useAuth } from "src/components/providers/auth-context";
import useAbortableStreamFetchDemo from "src/utils/request/use-abortable-stream-fetch-demo";
import { Helmet } from "react-helmet";
import fetchStatus from "../../utils/api/fetch-status";
import useAbortableStreamFetch from "../../utils/request/use-abortable-stream-fetch";

const SubtitleText = styled.span`
    font-size: 12px;
    font-weight: 400;
    color: ${({ theme }) => theme.palette.primary.main};
`;

const CaseAnalysis = () => {
    const [selectedCategory, setSelectedCategory] = React.useState("personal");
    const [selectedTeam, setSelectedTeam] = React.useState(" ");
    const [areChartsVisible, setAreChartsVisible] = React.useState(false);
    const [selectedAnalysis, setSelectedAnalysis] = React.useState("mr_duration_to_total");
    const [state, setState] = useState({
        status: fetchStatus.IDLE,
        errorCode: null,
        string: "",
        data: null,
    });
    const [controller] = useState(new AbortController());

    const { isDemo } = useAuth();

    const generateHandler = () => {
        setState({
            status: fetchStatus.IDLE,
            errorCode: null,
            string: "",
            data: null,
        });
        if (!isDemo) {
            let url = "";
            if (selectedCategory === "personal") {
                url = `/api/data/correlation`;
            } else if (selectedCategory === "team" && selectedTeam) {
                url = `/api/data/correlation/teams/${selectedTeam}`;
            } else if (selectedCategory === "organization") {
                url = `/api/data/correlation/organization`;
            }
            if (url) {
                useAbortableStreamFetch(
                    `${url}?correlation_type=${selectedAnalysis}`,
                    state,
                    controller,
                    setState
                );
            }
        }
        if (isDemo) {
            useAbortableStreamFetchDemo(selectedAnalysis, state, controller, setState);
        }
    };

    return (
        <TeamsProvider>
            <ActiveIntegrationsProvider>
                <Helmet>
                    <title>Adadot - Correlation Analysis</title>
                </Helmet>
                <Portal>
                    <DemoBarPersonal />
                    <DemoBarDemo />
                </Portal>
                <Stack direction="row" style={{ width: "100%" }} justifyContent="space-between">
                    <Stack direction="column">
                        <Typography id="modal-modal-title" variant="h6" component="h2">
                            Correlation analysis
                        </Typography>
                        <SubtitleText id="modal-modal-description" sx={{ mt: 2 }}>
                            Select a use case and see metrics correlation.
                        </SubtitleText>
                    </Stack>
                </Stack>
                <Stack
                    direction="column"
                    sx={{
                        width: "100%",
                        typography: "body1",
                        paddingTop: "26px",
                    }}
                    spacing={2}
                    justifyContent="space-between"
                >
                    <Typography
                        id="modal-modal-title"
                        style={{ fontSize: "12px", fontWeight: "200" }}
                    >
                        Select Category
                    </Typography>
                    <CaseAnalysisCategoryToggle
                        selectedCategory={selectedCategory}
                        setSelectedCategory={setSelectedCategory}
                    />
                </Stack>
                {selectedCategory === "team" && (
                    <TeamPicker selectedTeam={selectedTeam} setSelectedTeam={setSelectedTeam} />
                )}
                <Grid container md={12}>
                    <Grid item md={22}>
                        <Stack
                            direction="column"
                            sx={{
                                width: "100%",
                                typography: "body1",
                                paddingTop: "26px",
                            }}
                            spacing={2}
                            justifyContent="space-between"
                        >
                            <Typography
                                id="modal-modal-title"
                                style={{ fontSize: "12px", fontWeight: "200" }}
                            >
                                Select a case
                            </Typography>
                            <CaseAnalysisRadio
                                value={selectedAnalysis}
                                setValue={setSelectedAnalysis}
                                isDemo={isDemo}
                            />
                            <LoadingButton
                                variant="contained"
                                style={{ width: "180px" }}
                                onClick={() => generateHandler()}
                                loading={state.status === fetchStatus.STREAMING}
                                disabled={!selectedAnalysis}
                            >
                                Generate analysis
                            </LoadingButton>
                        </Stack>
                    </Grid>
                    <CaseAnalysisMainSection data={state.data} status={state.status} />
                    <CaseAnalysisGraphSection
                        areChartsVisible={areChartsVisible}
                        setAreChartsVisible={setAreChartsVisible}
                        data={state.data}
                    />
                </Grid>
            </ActiveIntegrationsProvider>
        </TeamsProvider>
    );
};

export default CaseAnalysis;
